/**
 * plazaHost.js — Single source of truth for "am I on a Plaza host?".
 *
 * Hosts the bundle treats as Plaza-only:
 *   • hustleplaza.au, www.hustleplaza.au          → public storefront
 *   • demo.hustleplaza.au, www.demo.hustleplaza.au → gated demo playbook
 *
 * Preview override (so you can see exactly what hustleplaza.au visitors
 * will see *before* DNS is cut over):
 *   ?as=plaza        → simulate the public Plaza host
 *   ?as=plaza-demo   → simulate the demo subdomain (still hits DemoGate)
 *   ?as=normal       → clear the override
 *
 * The override is persisted in sessionStorage so internal navigation
 * stays in the simulated mode without re-stamping ?as=… on every link.
 */
const PLAZA_HOSTS = new Set([
  "hustleplaza.au", "www.hustleplaza.au",
  // iter316m (2026-02) — plaza.au is the second apex for the same
  // 200k-item Plaza storefront; without this the SPA falls through
  // to the legacy tenant demo default when the browser hits
  // https://plaza.au/. Nginx already serves the same React bundle
  // for both hosts; this makes the client-side gate agree.
  "plaza.au", "www.plaza.au",
]);
const PLAZA_DEMO_HOSTS = new Set([
  "demo.hustleplaza.au",
  "www.demo.hustleplaza.au",
]);
const OVERRIDE_KEY = "hpz-host-override";

function readOverride() {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get("as");
    if (q === "plaza" || q === "plaza-demo" || q === "normal") {
      if (q === "normal") {
        sessionStorage.removeItem(OVERRIDE_KEY);
        // Also clear the moduleHost override (shared ?as= param).
        sessionStorage.removeItem("hs-module-host-override");
      } else {
        sessionStorage.setItem(OVERRIDE_KEY, q);
      }
      url.searchParams.delete("as");
      window.history.replaceState({}, "", url.toString());
      return q === "normal" ? null : q;
    }
    return sessionStorage.getItem(OVERRIDE_KEY);
  } catch { return null; }
}

/** Returns "plaza" | "plaza-demo" | null. */
export function getPlazaMode() {
  if (typeof window === "undefined") return null;
  const override = readOverride();
  if (override === "plaza" || override === "plaza-demo") return override;
  const host = (window.location.hostname || "").toLowerCase();
  if (PLAZA_DEMO_HOSTS.has(host)) return "plaza-demo";
  if (PLAZA_HOSTS.has(host)) return "plaza";
  return null;
}

export function isPlazaHost() {
  return getPlazaMode() !== null;
}
